<template>
  <div class="external-links-dialog">
    <div class="external-links-dialog-content">
      <div
        class="external-links-element"
        v-for="(elem, key) in getLinks"
        :key="key"
      >
        <a
          class="external-links-element__link"
          :href="elem.siteURL"
          target="_blank"
          :data-test="`header-link-externalLinks-${elem.id}`"
          @click="emits('closeRequested')"
        >
          <img
            class="external-links-element__image"
            :src="elem.imageURL"
            alt=""
          />
          <label class="external-links-element__label">{{ elem.label }}</label>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Config from "@/common/config";
import { HomepageSubPage, useHomepageLink } from "@/composables/homepageLink";
import egetransMarkImg from "@/assets/egetrans-mark.svg?url";
import shipmentTrackingMarkImg from "@/assets/shipment-tracking-mark.svg?url";
import etAcademyImg from "@/assets/et-academy-logo.svg?url";
import personioImg from "@/assets/personio-logo.svg?url";
import lighthouseImg from "@/assets/et-lighthouse.svg?url";

const emits = defineEmits<{
  (e: "closeRequested"): void;
}>();

const { getHomepageLink } = useHomepageLink();
const homePageURL = computed(() => getHomepageLink(HomepageSubPage.HOMEPAGE));

const getLinks = computed(() => {
  return Config.isCompass ? compassLinks : etcLinks;
});

type ExternalLinks = {
  imageURL: string;
  siteURL: string;
  label: string;
  id: string;
};

const etcLinks: ExternalLinks[] = [
  {
    imageURL: egetransMarkImg,
    siteURL: homePageURL.value,
    label: "EgeTrans",
    id: "homepage",
  },
  {
    imageURL: shipmentTrackingMarkImg,
    siteURL: Config.externalLinks.shipmentTracking,
    label: "Tracking ID",
    id: "tracking",
  },
];

const compassLinks: ExternalLinks[] = [
  {
    imageURL: lighthouseImg,
    siteURL: Config.externalLinks.lighthouse,
    label: "ET Lighthouse",
    id: "lighthouse",
  },
  {
    imageURL: personioImg,
    siteURL: Config.externalLinks.personio,
    label: "Personio",
    id: "personio",
  },
  {
    imageURL: etAcademyImg,
    siteURL: Config.externalLinks.etAcademy,
    label: "ET Campus",
    id: "academy",
  },
];
</script>

<style scoped lang="scss">
.external-links-dialog {
  z-index: 1000;
}

.external-links-dialog-content {
  position: relative;
  display: flex;
  justify-content: center;
  gap: 2.2rem;
  min-width: 16rem;
  padding: 2.2rem 2rem 2.5rem 1.2rem;
  background-color: white;
  border: 0.1rem solid #e3e6ec;
  border-top: 0;
}

.external-links-dialog-content::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 0.8rem;
  pointer-events: none;

  background: linear-gradient(
    to bottom,
    rgb(0 0 0 / 10%) 0%,
    rgb(0 0 0 / 7%) 10%,
    rgb(0 0 0 / 5%) 25%,
    rgb(0 0 0 / 3.5%) 45%,
    rgb(0 0 0 / 2%) 65%,
    rgb(0 0 0 / 1%) 80%,
    rgb(0 0 0 / 0.7%) 92%,
    rgb(0 0 0 / 0%) 100%
  );
}

.external-links-element {
  flex: 1 1 0;
  display: flex;
  justify-content: center;
}

.external-links-element__link {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  width: 100%;
  color: $color-blue-dark;
  text-decoration: none;
}

.external-links-element__link:hover {
  color: $color-etBlue;
}

.external-links-element__image {
  display: block;
  width: 48px;
  height: 48px;
  padding: 0.5rem;
  background: #f3f4f7;
  object-fit: contain;
}

.external-links-element__link:hover .external-links-element__image {
  background: #eef6ff;
}

.external-links-element__label {
  font-size: $font-size-base;
  font-weight: 400;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
}
</style>
